import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Col, Container, Row, Card, CardBody } from 'reactstrap';
import PropTypes from 'prop-types';
import oauthService from '../../../services/oauthService';
import { SiteName } from '../../../shared/components/domainSupport';
import LoadingSpinner from '../../../shared/components/widgets/LoadingSpinner';
import { jsonHeader } from '../../../config';

const UnifiedOAuthCallback = ({ platform, apiType = 'ad-api' }) => {
  const user = useSelector(state => state.authentication.user);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const completeOAuthFlow = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);

        // Extract OAuth parameters
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const errorParam = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        // Check for errors from OAuth provider
        if (errorParam) {
          setError(errorDescription || errorParam);
          setLoading(false);
          return;
        }

        // Check required parameters
        if (!code || !state) {
          setError('Missing required OAuth parameters (code or state)');
          setLoading(false);
          return;
        }

        // Extract platform-specific parameters
        const additionalParams = {};
        urlParams.forEach((value, key) => {
          if (!['code', 'state', 'error', 'error_description'].includes(key)) {
            additionalParams[key] = value;
          }
        });

        // Complete OAuth flow
        const headerWithAuth = {
          ...jsonHeader,
          authorization: user?.jwtToken ? `JWT ${user.jwtToken}` : undefined,
        };

        const data = await oauthService.completeOAuth({
          platform,
          apiType,
          code,
          state,
          additionalParams,
          options: {
            method: 'POST',
            headers: headerWithAuth,
          },
        });

        setResult(data);
        setSuccess(true);
      } catch (err) {
        console.error('OAuth completion error:', err);
        setError(err.message || 'Failed to complete OAuth authorization');
      } finally {
        setLoading(false);
      }
    };

    completeOAuthFlow();
  }, [platform, apiType, user]);

  const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
  const supportText = SiteName() === 'Limitless' ? (
    <h4>For onboarding support, please contact <a href="mailto:adteam@limitless.tech">adteam@limitless.tech</a></h4>
  ) : null;

  return (
    <Container className="dashboard">
      <Row>
        <Col md={12}>
          <h3 className="page-title">
            {platformName} OAuth - API Onboarding
          </h3>
        </Col>
      </Row>
      <Row><br /></Row>
      <Row>
        <Col md={12}>
          <div className="account__wrapper">
            <LoadingSpinner active={loading} />

            {!loading && error && (
              <>
                <h4 className="text-danger">Authorization Error</h4>
                <p>There was an error during the OAuth authorization process:</p>
                <br />
                <Card>
                  <CardBody>
                    <h5 className="bold-text text-danger">Error Details:</h5>
                    <p>{error}</p>
                  </CardBody>
                </Card>
                <br />
                {supportText}
              </>
            )}

            {!loading && !error && success && (
              <>
                <h4 className="text-success">✓ Authorization Successful!</h4>
                <p>Your {platformName} account has been successfully authorized and attached.</p>
                <br />
                <Card>
                  <CardBody>
                    <h5 className="bold-text">Authorization Details:</h5>
                    {result?.metadata?.accountName && <p><strong>Account:</strong> {result.metadata.accountName}</p>}
                    {result?.message && <p>{result.message}</p>}
                  </CardBody>
                </Card>
                <br />
                {supportText}
                <br />
                <h4>Thank You!</h4>
              </>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

UnifiedOAuthCallback.propTypes = {
  platform: PropTypes.string.isRequired,
  apiType: PropTypes.string,
};

UnifiedOAuthCallback.defaultProps = {
  apiType: 'ad-api',
};

export default UnifiedOAuthCallback;
